/*
 * Hiring v2 phase 2 (owner, 2026-11-04): after the phone call, the applicant's same page (/a/<token>) walks them through
 * the paid trial day, signing the offer, and the paperwork (W-9, emergency contact, ID photo, handbook, short lessons), in
 * English or Spanish. The lead's trial day scorecard (/t/<token>) is here too. Plain words, made for a phone.
 */
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import {
  TRIAL_SCORES,
  TRIAL_SCORE_WORDS,
  type ApplyBooking,
  type ApplyLang,
  type TrialScorePage,
  type TrialScores,
} from "@steward/shared";
import { api } from "../api/client";
import { InputText } from "../components/fields";
import { Banner, Button, Skeleton } from "../components/parts";
import { SignaturePad } from "../components/SignaturePad";
import { errText } from "../components/work";
import { Branded, NotFound } from "./public/common";

const J = {
  en: {
    trialTitle: "Your paid trial day",
    trialLead: (who: string) => `You'll ride with ${who} and learn how we work.`,
    trialPay: (rate: string) => `You're paid ${rate} an hour for the day.`,
    trialOk: "Yes, I'll be there",
    trialConfirmed: "You said you'll be there. See you then!",
    offerTitle: "Your offer",
    signName: "Type your full name",
    sign: "Sign my offer",
    paperTitle: "A few last things",
    paperLead: "Finish these and we'll text you the crew app invite.",
    w9: "Tax form (W-9)",
    w9Why: "We pay you as a contractor, so we need this for your 1099 each year. Your number is kept private.",
    legalName: "Name on your tax return",
    business: "Business name (if you have one)",
    classification: "You are",
    individual: "A person or sole owner",
    llc: "An LLC",
    tinType: "Your tax number is",
    ssn: "Social Security number",
    ein: "EIN (business number)",
    tin: "The number",
    address1: "Street address",
    address2: "Apt or unit (if any)",
    city: "City",
    state: "State",
    zip: "ZIP",
    certify:
      "By typing your name you say this number is right, you are not subject to backup withholding, and you are a US person, as on the IRS W-9.",
    w9Sign: "Type your full name to sign",
    save: "Save",
    contact: "Emergency contact and shirt",
    contactName: "Who we call in an emergency",
    contactPhone: "Their phone",
    relation: "How you know them",
    shirt: "Shirt size",
    id: "Photo of your ID",
    idWhy: "A driver's license or any photo ID. Kept private.",
    idPick: "Take or pick a photo",
    handbook: "Read the handbook",
    handbookOpen: "Open the handbook",
    handbookDone: "I read it",
    lessons: "Short lessons",
    check: "Check my answer",
    done: "Done",
    allDone: "Welcome to the team!",
    allDoneLead: "Watch for a text with your crew app invite. We're glad you're here.",
    paperDone: "All done!",
    paperDoneLead: "We'll text you the crew app invite soon.",
    bgTitle: "Background check.",
    bgLead: "Fill it out on Checkr's page. It takes about 10 minutes.",
    bgOpen: "Start my background check",
    bgWait: "We'll text you the crew app invite when your background check is back.",
  },
  es: {
    trialTitle: "Tu día de prueba pagado",
    trialLead: (who: string) => `Vas a ir con ${who} y aprender cómo trabajamos.`,
    trialPay: (rate: string) => `Te pagamos ${rate} la hora por ese día.`,
    trialOk: "Sí, ahí estaré",
    trialConfirmed: "Dijiste que vas a estar. ¡Nos vemos!",
    offerTitle: "Tu oferta",
    signName: "Escribe tu nombre completo",
    sign: "Firmar mi oferta",
    paperTitle: "Unas últimas cosas",
    paperLead: "Termina esto y te mandamos la invitación a la app del equipo.",
    w9: "Formulario de impuestos (W-9)",
    w9Why: "Te pagamos como contratista, así que lo necesitamos para tu 1099 cada año. Tu número se guarda en privado.",
    legalName: "Nombre en tu declaración de impuestos",
    business: "Nombre del negocio (si tienes)",
    classification: "Eres",
    individual: "Una persona o dueño único",
    llc: "Una LLC",
    tinType: "Tu número de impuestos es",
    ssn: "Seguro Social",
    ein: "EIN (número de negocio)",
    tin: "El número",
    address1: "Dirección",
    address2: "Apartamento (si tienes)",
    city: "Ciudad",
    state: "Estado",
    zip: "Código postal",
    certify:
      "Al escribir tu nombre dices que este número es correcto, que no estás sujeto a retención adicional y que eres persona de EE. UU., como en el W-9 del IRS.",
    w9Sign: "Escribe tu nombre completo para firmar",
    save: "Guardar",
    contact: "Contacto de emergencia y camisa",
    contactName: "A quién llamamos en una emergencia",
    contactPhone: "Su teléfono",
    relation: "Cómo lo conoces",
    shirt: "Talla de camisa",
    id: "Foto de tu identificación",
    idWhy: "Licencia de manejar o cualquier identificación con foto. Se guarda en privado.",
    idPick: "Toma o escoge una foto",
    handbook: "Lee el manual",
    handbookOpen: "Abrir el manual",
    handbookDone: "Ya lo leí",
    lessons: "Lecciones cortas",
    check: "Revisar mi respuesta",
    done: "Listo",
    allDone: "¡Bienvenido al equipo!",
    allDoneLead: "Espera un texto con la invitación a la app del equipo. Qué bueno tenerte.",
    paperDone: "¡Todo listo!",
    paperDoneLead: "Pronto te mandamos la invitación a la app del equipo.",
    bgTitle: "Revisión de antecedentes.",
    bgLead: "Llénala en la página de Checkr. Toma unos 10 minutos.",
    bgOpen: "Empezar mi revisión",
    bgWait: "Te mandamos la invitación a la app del equipo cuando regrese tu revisión de antecedentes.",
  },
};

const money = (cents: number) => `$${(cents / 100).toFixed(2).replace(/\.00$/, "")}`;

/** One step card: a title, done or not, and its form when open. */
function Section({
  title,
  done,
  open,
  onOpen,
  children,
}: {
  title: string;
  done: boolean;
  open: boolean;
  onOpen: () => void;
  children: ReactNode;
}) {
  return (
    <section className="careers-step">
      <button type="button" className="careers-step-head" aria-expanded={open} onClick={onOpen}>
        <span className={done ? "careers-check done" : "careers-check"} aria-hidden="true">
          {done ? "✓" : ""}
        </span>
        <b>{title}</b>
      </button>
      {open ? <div className="stack careers-step-body">{children}</div> : null}
    </section>
  );
}

/** The trial day, offer, paperwork or welcome, for a page past the phone call. */
export function Journey({ d, token, onNext }: { d: ApplyBooking; token: string; onNext: (b: ApplyBooking) => void }) {
  const lang: ApplyLang = d.language;
  const w = J[lang];
  const j = d.journey;
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const post = async (what: string, path: string, body?: unknown) => {
    setBusy(what);
    setErr(null);
    try {
      onNext(await api<ApplyBooking>("POST", `public/apply/${token}/${path}`, body));
      return true;
    } catch (e) {
      setErr(errText(e));
      return false;
    } finally {
      setBusy(null);
    }
  };
  const when = (iso: string) =>
    new Date(iso).toLocaleString(lang === "es" ? "es-US" : "en-US", {
      timeZone: d.timezone,
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  if (j.step === "done")
    return (
      <>
        <h1>{w.allDone}</h1>
        <p className="rv-lead">{w.allDoneLead}</p>
      </>
    );
  if (j.step === "trial" && j.trial)
    return (
      <>
        <h1>{w.trialTitle}</h1>
        <p className="rv-lead">{when(j.trial.at)}</p>
        <p>{j.trial.address}</p>
        <p>{w.trialLead(j.trial.lead)}</p>
        {j.trial.ratePerHourCents > 0 ? <p className="muted">{w.trialPay(money(j.trial.ratePerHourCents))}</p> : null}
        {err ? <Banner type="error">{err}</Banner> : null}
        {j.trial.confirmed ? (
          <Banner type="success">{w.trialConfirmed}</Banner>
        ) : (
          <Button size="large" fullWidth loading={busy === "trial"} onClick={() => void post("trial", "trial-ok")}>
            {w.trialOk}
          </Button>
        )}
      </>
    );
  if (j.step === "offer" && j.offer)
    return <OfferStep d={d} w={w} busy={busy === "sign"} err={err} sign={(b) => void post("sign", "sign", b)} />;
  return <Paperwork d={d} w={w} busy={busy} err={err} post={post} />;
}

function OfferStep({
  d,
  w,
  busy,
  err,
  sign,
}: {
  d: ApplyBooking;
  w: (typeof J)["en"];
  busy: boolean;
  err: string | null;
  sign: (b: { name: string; drawing: string | null }) => void;
}) {
  const [name, setName] = useState("");
  const [drawing, setDrawing] = useState<string | null>(null);
  return (
    <>
      <h1>{w.offerTitle}</h1>
      <div className="careers-letter">{d.journey.offer?.letter}</div>
      <InputText label={w.signName} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
      <SignaturePad onChange={setDrawing} />
      {err ? <Banner type="error">{err}</Banner> : null}
      <Button
        size="large"
        fullWidth
        loading={busy}
        disabled={name.trim().length < 2}
        onClick={() => sign({ name: name.trim(), drawing })}
      >
        {w.sign}
      </Button>
    </>
  );
}

function Paperwork({
  d,
  w,
  busy,
  err,
  post,
}: {
  d: ApplyBooking;
  w: (typeof J)["en"];
  busy: string | null;
  err: string | null;
  post: (what: string, path: string, body?: unknown) => Promise<boolean>;
}) {
  const p = d.journey.paperwork;
  const first = p.left[0] ?? null;
  const [open, setOpen] = useState<string | null>(first);
  const next = async (what: string, path: string, body?: unknown) => {
    if (await post(what, path, body)) setOpen(null);
  };
  // Phase 3: their background check link while it waits on them (Checkr's own page).
  const bg = d.journey.background;
  const bgBox =
    bg?.waiting && bg.url ? (
      <Banner type="notice">
        <b>{w.bgTitle}</b> {w.bgLead}{" "}
        <a className="link" href={bg.url} target="_blank" rel="noreferrer">
          {w.bgOpen}
        </a>
      </Banner>
    ) : null;
  if (p.left.length === 0)
    return (
      <>
        <h1>{w.paperDone}</h1>
        <p className="rv-lead">{bg?.waiting ? w.bgWait : w.paperDoneLead}</p>
        {bgBox}
      </>
    );
  return (
    <>
      <h1>{w.paperTitle}</h1>
      <p className="rv-lead">{w.paperLead}</p>
      {bgBox}
      {err ? <Banner type="error">{err}</Banner> : null}
      <Section title={w.w9} done={p.w9} open={open === "w9"} onOpen={() => setOpen(open === "w9" ? null : "w9")}>
        <W9Form w={w} busy={busy === "w9"} save={(b) => void next("w9", "w9", b)} />
      </Section>
      <Section
        title={w.contact}
        done={p.contact}
        open={open === "contact"}
        onOpen={() => setOpen(open === "contact" ? null : "contact")}
      >
        <ContactForm w={w} busy={busy === "contact"} save={(b) => void next("contact", "contact", b)} />
      </Section>
      <Section title={w.id} done={p.id} open={open === "id"} onOpen={() => setOpen(open === "id" ? null : "id")}>
        <p className="muted">{w.idWhy}</p>
        <label className="btn secondary">
          {busy === "id" ? "…" : w.idPick}
          <input
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const r = new FileReader();
              r.onload = () => {
                const data = (typeof r.result === "string" ? r.result : "").split(",")[1] ?? "";
                void next("id", "id", { name: f.name, mime: f.type || "image/jpeg", dataBase64: data });
              };
              r.readAsDataURL(f);
            }}
          />
        </label>
      </Section>
      {p.handbookUrl ? (
        <Section
          title={w.handbook}
          done={p.handbook}
          open={open === "handbook"}
          onOpen={() => setOpen(open === "handbook" ? null : "handbook")}
        >
          <a className="btn secondary" href={p.handbookUrl} target="_blank" rel="noreferrer">
            {w.handbookOpen}
          </a>
          <Button loading={busy === "handbook"} onClick={() => void next("handbook", "handbook")}>
            {w.handbookDone}
          </Button>
        </Section>
      ) : null}
      <Section
        title={`${w.lessons} (${String(p.lessons.filter((l) => l.done).length)} / ${String(p.lessons.length)})`}
        done={p.lessons.every((l) => l.done)}
        open={open === "lessons"}
        onOpen={() => setOpen(open === "lessons" ? null : "lessons")}
      >
        <Lessons d={d} w={w} busy={busy} post={post} />
      </Section>
    </>
  );
}

function Lessons({
  d,
  w,
  busy,
  post,
}: {
  d: ApplyBooking;
  w: (typeof J)["en"];
  busy: string | null;
  post: (what: string, path: string, body?: unknown) => Promise<boolean>;
}) {
  const l = d.journey.paperwork.lessons.find((x) => !x.done);
  const [pick, setPick] = useState<number | null>(null);
  if (!l) return <p>{w.done} ✓</p>;
  return (
    <div className="stack">
      <b>{l.title}</b>
      <p>{l.body}</p>
      <div className="field">
        <span className="label">{l.question}</span>
        <div className="choice" role="radiogroup" aria-label={l.question}>
          {l.choices.map((c, i) => (
            <button key={c} type="button" role="radio" aria-checked={pick === i} onClick={() => setPick(i)}>
              <b>{c}</b>
            </button>
          ))}
        </div>
      </div>
      <Button
        loading={busy === `lesson-${l.id}`}
        disabled={pick === null}
        onClick={() => {
          if (pick === null) return;
          void post(`lesson-${l.id}`, "lesson", { id: l.id, choice: pick }).then((ok) => {
            if (ok) setPick(null);
          });
        }}
      >
        {w.check}
      </Button>
    </div>
  );
}

function W9Form({ w, busy, save }: { w: (typeof J)["en"]; busy: boolean; save: (b: Record<string, unknown>) => void }) {
  const [f, setF] = useState({
    legalName: "",
    business: "",
    classification: "individual" as "individual" | "llc",
    tinType: "ssn" as "ssn" | "ein",
    tin: "",
    address1: "",
    address2: "",
    city: "",
    state: "LA",
    zip: "",
    signedName: "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="stack">
      <p className="muted">{w.w9Why}</p>
      <InputText label={w.legalName} value={f.legalName} onChange={set("legalName")} />
      <InputText label={w.business} value={f.business} onChange={set("business")} />
      <div className="field">
        <span className="label">{w.classification}</span>
        <div className="choice" role="radiogroup" aria-label={w.classification}>
          {(["individual", "llc"] as const).map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={f.classification === c}
              onClick={() => setF({ ...f, classification: c })}
            >
              <b>{w[c]}</b>
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span className="label">{w.tinType}</span>
        <div className="choice" role="radiogroup" aria-label={w.tinType}>
          {(["ssn", "ein"] as const).map((c) => (
            <button key={c} type="button" role="radio" aria-checked={f.tinType === c} onClick={() => setF({ ...f, tinType: c })}>
              <b>{w[c]}</b>
            </button>
          ))}
        </div>
      </div>
      <InputText label={w.tin} inputMode="numeric" autoComplete="off" value={f.tin} onChange={set("tin")} />
      <InputText label={w.address1} autoComplete="address-line1" value={f.address1} onChange={set("address1")} />
      <InputText label={w.address2} autoComplete="address-line2" value={f.address2} onChange={set("address2")} />
      <InputText label={w.city} autoComplete="address-level2" value={f.city} onChange={set("city")} />
      <div className="row">
        <InputText label={w.state} maxLength={2} value={f.state} onChange={set("state")} />
        <InputText label={w.zip} inputMode="numeric" maxLength={5} value={f.zip} onChange={set("zip")} />
      </div>
      <p className="muted">{w.certify}</p>
      <InputText label={w.w9Sign} autoComplete="name" value={f.signedName} onChange={set("signedName")} />
      <Button
        loading={busy}
        onClick={() =>
          save({
            ...f,
            business: f.business.trim() || undefined,
            address2: f.address2.trim() || undefined,
          })
        }
      >
        {w.save}
      </Button>
    </div>
  );
}

function ContactForm({ w, busy, save }: { w: (typeof J)["en"]; busy: boolean; save: (b: Record<string, unknown>) => void }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [relation, setRelation] = useState("");
  const [shirt, setShirt] = useState("M");
  return (
    <div className="stack">
      <InputText label={w.contactName} value={name} onChange={(e) => setName(e.target.value)} />
      <InputText label={w.contactPhone} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      <InputText label={w.relation} value={relation} onChange={(e) => setRelation(e.target.value)} />
      <div className="field">
        <span className="label">{w.shirt}</span>
        <div className="chips">
          {["S", "M", "L", "XL", "2XL", "3XL"].map((s) => (
            <button key={s} type="button" className="chip" aria-pressed={shirt === s} onClick={() => setShirt(s)}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <Button loading={busy} onClick={() => save({ name, phone, relation, shirt })}>
        {w.save}
      </Button>
    </div>
  );
}

/** The lead's scorecard for a trial day (/t/<token>), in the lead's language. */
export function TrialScoreView({ token }: { token: string }) {
  const page = useQuery({ queryKey: ["trial-score", token], queryFn: () => api<TrialScorePage>("GET", `public/trial/${token}`) });
  const [scores, setScores] = useState<TrialScores>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  if (page.error) return <NotFound what="page" />;
  const d = page.data;
  if (!d)
    return (
      <Branded brand={null}>
        <Skeleton rows={4} />
      </Branded>
    );
  const es = d.language === "es";
  const words = TRIAL_SCORE_WORDS[d.language];
  const done = sent || d.scored;
  return (
    <Branded brand={d.brand} title={d.brand.name}>
      <div className="careers">
        {done ? (
          <>
            <h1>{es ? "¡Gracias!" : "Thank you!"}</h1>
            <p className="rv-lead">{es ? "Tu calificación ya le llegó a la oficina." : "Your score went to the office."}</p>
          </>
        ) : (
          <>
            <h1>{es ? `¿Cómo le fue a ${d.first}?` : `How did ${d.first} do?`}</h1>
            <p className="rv-lead">{es ? "1 es malo, 5 es excelente." : "1 is poor, 5 is great."}</p>
            {TRIAL_SCORES.map((k) => (
              <div key={k} className="field">
                <span className="label">{words[k]}</span>
                <div className="chips">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className="chip"
                      aria-pressed={scores[k] === n}
                      onClick={() => setScores({ ...scores, [k]: n })}
                    >
                      {String(n)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <label className="field">
              <span className="label">{es ? "Algo más (si quieres)" : "Anything else (if you want)"}</span>
              <textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            {err ? <Banner type="error">{err}</Banner> : null}
            <Button
              size="large"
              fullWidth
              loading={busy}
              disabled={Object.keys(scores).length < TRIAL_SCORES.length}
              onClick={() => {
                setBusy(true);
                setErr(null);
                api("POST", `public/trial/${token}`, { scores, ...(note.trim() ? { note: note.trim() } : {}) })
                  .then(() => setSent(true))
                  .catch((e: unknown) => setErr(errText(e)))
                  .finally(() => setBusy(false));
              }}
            >
              {es ? "Enviar" : "Send"}
            </Button>
          </>
        )}
      </div>
    </Branded>
  );
}
